import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { eventService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import eventBg from '../assets/event-bg.png';
import MessagesSidebar from '../components/MessagesSidebar';

const Event = () => {
    const { slug } = useParams();
    const { language } = useLanguage();
    const navigate = useNavigate();

    const [activeMenu, setActiveMenu] = useState(null);
    const [activeSubMenu, setActiveSubMenu] = useState(null);
    const [showMore, setShowMore] = useState(false);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [eventData, setEventData] = useState(null);

    // ... (menuItems dizisi aynı kalabilir)
    const menuItems = [
        {
            id: 1,
            title: language === 'fa' ? 'ثبت نام در نمایشگاه' : 'Exhibition Registration',
            hasSubmenu: true,
            subItems: [
                language === 'fa' ? 'امور مالی' : 'Financial Affairs',
                language === 'fa' ? 'پیام ها' : 'Messages'
            ]
        },
        {
            id: 2,
            title: language === 'fa' ? 'حساب کاربری' : 'User Account',
            hasSubmenu: false
        },
        {
            id: 3,
            title: language === 'fa' ? 'پشتیبانی' : 'Support',
            hasSubmenu: false
        },
        {
            id: 4,
            title: language === 'fa' ? 'خروج از حساب کاربری' : 'Logout',
            hasSubmenu: false
        }
    ];

    useEffect(() => {
        const fetchEventData = async () => {
            setLoading(true);
            setError(null);
            try {
                const eventSlug = slug || 'test11111';
                const data = await eventService.getEventBySlug(eventSlug);
                setEventData(data);
            } catch (err) {
                console.error('Event API error:', err);
                setError(language === 'fa' ? 'خطا در دریافت اطلاعات رویداد' : 'Error fetching event data');
            } finally {
                setLoading(false);
            }
        };
        fetchEventData();
    }, [slug, language]);

    // Belirtilen tipteki submission'ı döndürür. Yoksa undefined döner.
    const getSubmissionByType = (type) => {
        if (!eventData?.submissions) return undefined; // Eğer submissions dizisi hiç yoksa undefined döndür.
        return eventData.submissions.find(s => s.type === type);
    };

    const infoSubmission = getSubmissionByType(0); // type 0: Information Form
    const boothSubmission = getSubmissionByType(1); // type 1: Booth Form

    // Güncellenmiş Durum Metinleri
    const getSubmissionStatusText = (submission) => {
        if (!submission) return '';
        if (language === 'fa') {
            const statusMap = { 0: 'ثبت نام', 1: 'در انتظار تایید', 2: 'تایید شده', 3: 'عدم تایید' };
            return statusMap[submission.status] || '';
        } else {
            const statusMap = { 0: 'Register', 1: 'Pending', 2: 'Approved', 3: 'Rejected' };
            return statusMap[submission.status] || '';
        }
    };

    // Durumlara göre arka plan rengi (opsiyonel, badge için kullanılabilir)
    const getStatusBgColor = (submission) => {
        if (!submission) return '';
        const colorMap = { 0: 'bg-blue-500', 1: 'bg-yellow-500', 2: 'bg-green-500', 3: 'bg-red-500' };
        return colorMap[submission.status] || 'bg-gray-500';
    };

    // Alt butonların metinlerini döndüren yardımcı fonksiyonlar
    const getViewInfoText = () => language === 'fa' ? 'مشاهده اطلاعات' : 'View Information';
    const getUpdateInfoText = () => language === 'fa' ? 'ویرایش اطلاعات' : 'Update Information';
    const getSubmitInfoText = () => language === 'fa' ? 'ثبت اطلاعات' : 'Submit Information';
    const getRequestBoothText = () => language === 'fa' ? 'درخواست غرفه' : 'Request Booth';
    const getStatusLabelText = () => language === 'fa' ? 'وضعیت' : 'Status';

    // ... (getAnnouncements, formatDate fonksiyonları aynı kalır)
    const getAnnouncements = () => {
        if (!eventData?.announcements) return [];
        return eventData.announcements.map(announcement => ({
            id: announcement.slug || Math.random(),
            text: announcement.title || '',
            description: announcement.description || '',
            created_at: announcement.created_at,
            attachment: announcement.attachment,
            venue: announcement.text
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const diffMonths = Math.floor(diffDays / 30);

        if (language === 'fa') {
            if (diffMonths > 0) return `${diffMonths} ماه پیش`;
            if (diffDays > 0) return `${diffDays} روز پیش`;
            return 'امروز';
        } else {
            if (diffMonths > 0) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
            if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
            return 'Today';
        }
    };

    const handleMenuClick = (menuId) => {
        setActiveMenu(activeMenu === menuId ? null : menuId);
        setActiveSubMenu(null);
    };

    const handleSubMenuClick = (subIndex) => {
        setActiveSubMenu(subIndex);
    };

    // Loading ve Error durumları aynı kalır...
    if (loading) {
        return (
            <div className="max-w-[1136px] mx-auto px-4 py-8 font-iransansRegular">
                {/* ... spinner ... */}
                <div className="flex justify-center items-center py-20">
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 border-4 border-[#3336B7] border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-[#3336B7] font-bold text-lg">{language === 'fa' ? 'در حال بارگذاری...' : 'Loading...'}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-[1136px] mx-auto px-4 py-8 font-iransansRegular">
                {/* ... error mesajı ... */}
                <div className="flex justify-center items-center py-20">
                    <div className="flex flex-col items-center gap-4 bg-red-50 rounded-xl p-6">
                        <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <p className="text-red-600 font-bold text-lg">{error}</p>
                        <button onClick={() => window.location.reload()} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">
                            {language === 'fa' ? 'تلاش مجدد' : 'Try Again'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const announcements = getAnnouncements();
    const eventTitle = eventData?.title || '';
    const eventDescription = eventData?.text || '';
    const eventVenue = eventData?.description || '';

    return (
        <div className="max-w-[1136px] mx-auto px-4 py-8 font-iransansRegular">
            {/* Mobil Menü (Aynı) */}
            <div className="flex lg:hidden gap-4 mb-6">
               <MessagesSidebar
                    activeKey=""
                    onNavigate={(key) => {
                        if (key === 'messages') navigate('/messages');
                        if (key === 'finance') navigate('/finance');
                        if (key === 'account') navigate('/account');
                        if (key === 'support') navigate('/support');
                        if (key === 'logout') navigate('/logout');
                    }}
                />
                <div className="w-full">
                    <img src={eventData?.poster || eventData?.banner || "/event-banner.png"} alt="Event" className="w-full rounded-lg" />
                </div>
            </div>

            <div className="flex flex-col-reverse lg:flex-row-reverse gap-12">
                {/* Sol Sekme - Duyurular (Aynı) */}
                <div className="hidden lg:block w-full lg:w-[272px] shrink-0">
                    <img src={eventData?.poster || eventData?.banner || "/event-banner.png"} alt="Event" className="w-full rounded-lg" />
                    <div className="w-full mt-6 py-2 rounded-xl flex justify-center items-center bg-[#D4DBF8]">
                        <h3 className="text-[20px] font-extrabold text-[#3336B7]">{language === 'fa' ? 'اطلاعیه های نمایشگاه' : 'Announcements'}</h3>
                    </div>
                    <div className="mt-4 space-y-3">
                        {announcements.length > 0 ? (
                            announcements.map((announcement) => (
                                <div key={announcement.id} className="border-2 border-[#3336B7] rounded-xl py-[6px] px-3 w-full flex justify-between items-center">
                                    <p className={`text-[14px] font-normal text-[#002450] w-[190px] leading-relaxed'}`}>{announcement.text}</p>
                                    <div className="flex items-center gap-2">
                                        <div className="w-[1px] h-4 bg-gray-400"></div>
                                        <img src="/clock.svg" alt="clock" className="w-4 h-4" />
                                        <span className="text-[10px] font-normal text-[#002450] whitespace-nowrap">{formatDate(announcement.created_at)}</span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-center text-gray-500">{language === 'fa' ? 'اطلاعیه‌ای موجود نیست' : 'No announcements'}</p>
                        )}
                    </div>
                </div>

                {/* Orta Sekme */}
                <div className="w-full lg:w-[496px] shrink-0">
                    <h1 className="text-[20px] lg:text-[38px] font-bold text-[#3336B7] leading-tight mb-4">{eventTitle}</h1>
                    {eventVenue && <p className={`transition-all duration-500 ${showMore ? '' : 'line-clamp-2'}`}>{eventVenue}</p>}
                    {/* <p className={`text-xs lg:text-2xl font-bold text-[#002450] leading-relaxed mb-3 transition-all duration-500 ${showMore ? '' : 'line-clamp-2'}`}>{eventDescription}</p> */}
                    {showMore && (
                        <div className="animate-fadeIn">
                            <div className="w-full bg-gray-200 rounded-lg mb-6 flex items-center justify-center min-h-[200px] lg:min-h-[300px]">
                                {eventData?.image ? <img src={eventData.image} alt="Event" className="w-full h-full object-cover rounded-lg" /> : <span className="text-gray-400 text-sm lg:text-lg">{language === 'fa' ? 'تصویر نمایشگاه' : 'Exhibition Image'}</span>}
                            </div>
                            <div className="text-sm lg:text-base text-gray-700 leading-relaxed"><p>{eventDescription}</p></div>
                        </div>
                    )}
                    <button onClick={() => setShowMore(!showMore)} className="text-sm lg:text-[20px] font-bold text-[#FFB947] hover:text-[#e5a63a] transition-colors duration-300 mb-6">
                        {showMore ? (language === 'fa' ? 'بستن...' : 'Close...') : (language === 'fa' ? 'بیشتر...' : 'More...')}
                    </button>

                    <img src="/wing.png" alt="" />

                    {/* GÜNCELLENMİŞ: Information Form Card - Sadece infoSubmission mevcutsa gösterilir */}
                    {/* Information Form Card - type 0 */}
                    {infoSubmission !== undefined && (
                        <div
                            className={`px-4 flex justify-between items-center py-2 mt-4 rounded-xl transition-all duration-300 hover:scale-[1.01] hover:shadow-xl ${infoSubmission?.status === 0 ? 'cursor-pointer' : 'cursor-default'}`}
                            style={{ backgroundImage: `url(${eventBg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: '136px', width: '100%' }}
                            onClick={() => {
                                if (infoSubmission.status === 0) navigate(`/events/${slug}/form?type=0`);
                            }}
                        >
                            <div className="font-iransansExtraBold">
                                <p className="text-white text-2xl drop-shadow-md">
                                    {getSubmitInfoText()}
                                </p>
                                <div className="flex mt-2 space-x-2 space-x-reverse flex-wrap gap-y-2">
                                    {infoSubmission.status === 0 ? (
                                        <div className="bg-white text-xs font-iransansExtraBold text-[#204286] px-3 py-2 rounded-lg transition-all duration-200 hover:bg-[#204286] hover:text-white hover:scale-105 cursor-pointer shadow-md"
                                            onClick={(e) => { e.stopPropagation(); navigate(`/events/${slug}/form?type=0`); }}>
                                            <p>{getSubmitInfoText()}</p>
                                        </div>
                                    ) : (
                                        <>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); navigate(`/events/${slug}/form?view=true&type=0`); }}
                                                className="bg-white text-xs font-iransansExtraBold text-[#204286] px-3 py-2 rounded-lg transition-all duration-200 hover:bg-[#204286] hover:text-white hover:scale-105 shadow-md"
                                            >
                                                {getViewInfoText()}
                                            </button>
                                            {infoSubmission.status === 3 && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/events/${slug}/form?edit=true&type=0`); }}
                                                    className="bg-yellow-500 text-xs font-iransansExtraBold text-white px-3 py-2 rounded-lg transition-all duration-200 hover:bg-yellow-600 hover:scale-105 shadow-md"
                                                >
                                                    {getUpdateInfoText()}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="text-white w-[67px] md:w-[120px] text-sm md:text-2xl font-iransansBold drop-shadow-md text-center leading-tight">
                                <p className="text-white text-lg drop-shadow-md mb-1">{getStatusLabelText()}</p>
                                <span className={`${getStatusBgColor(infoSubmission)} text-white text-xs px-2 py-1 rounded-lg`}>
                                    {getSubmissionStatusText(infoSubmission)}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Booth Form Card - type 1 */}
                    {boothSubmission !== undefined && (
                        <div
                            className={`px-4 flex justify-between items-center py-2 mt-4 rounded-xl transition-all duration-300 hover:scale-[1.01] hover:shadow-xl ${boothSubmission?.status === 0 ? 'cursor-pointer' : 'cursor-default'}`}
                            style={{ backgroundImage: `url(${eventBg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', height: '136px', width: '100%' }}
                            onClick={() => {
                                if (boothSubmission.status === 0) navigate(`/events/${slug}/form?type=1`);
                            }}
                        >
                            <div className="font-iransansExtraBold">
                                <p className='text-white text-2xl font-iransansExtraBold'>{getRequestBoothText()}</p>
                                <div className="flex mt-2 space-x-2 space-x-reverse flex-wrap gap-y-2">
                                    {boothSubmission.status === 0 ? (
                                        <div className="bg-white text-xs font-iransansExtraBold text-[#204286] px-3 py-2 rounded-lg transition-all duration-200 hover:bg-[#204286] hover:text-white hover:scale-105 cursor-pointer shadow-md"
                                            onClick={(e) => { e.stopPropagation(); navigate(`/events/${slug}/form?type=1`); }}>
                                            <p>{getRequestBoothText()}</p>
                                        </div>
                                    ) : (
                                        <>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); navigate(`/events/${slug}/form?view=true&type=1`); }}
                                                className="bg-white text-xs font-iransansExtraBold text-[#204286] px-3 py-2 rounded-lg transition-all duration-200 hover:bg-[#204286] hover:text-white hover:scale-105 shadow-md"
                                            >
                                                {getViewInfoText()}
                                            </button>
                                            {boothSubmission.status === 3 && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/events/${slug}/form?edit=true&type=1`); }}
                                                    className="bg-yellow-500 text-xs font-iransansExtraBold text-white px-3 py-2 rounded-lg transition-all duration-200 hover:bg-yellow-600 hover:scale-105 shadow-md"
                                                >
                                                    {getUpdateInfoText()}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="text-white w-[67px] md:w-[120px] text-sm md:text-2xl font-iransansBold drop-shadow-md text-center leading-tight">
                                <p className="text-white text-lg drop-shadow-md mb-1">{getStatusLabelText()}</p>
                                <span className={`${getStatusBgColor(boothSubmission)} text-white text-xs px-2 py-1 rounded-lg`}>
                                    {getSubmissionStatusText(boothSubmission)}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Mobil Duyurular (Aynı) */}
                    <div className="lg:hidden mt-6">
                        <div className="w-full py-2 rounded-xl flex justify-center items-center bg-[#D4DBF8]">
                            <h3 className="text-[20px] font-extrabold text-[#3336B7]">{language === 'fa' ? 'اطلاعیه های نمایشگاه' : 'Announcements'}</h3>
                        </div>
                        <div className="mt-4 space-y-3">
                            {announcements.length > 0 ? (
                                announcements.map((announcement) => (
                                    <div key={announcement.id} className="border-2 border-[#3336B7] rounded-xl py-[6px] px-3 w-full flex justify-between items-center">
                                        <p className="text-[14px] font-normal text-[#002450] flex-1 leading-relaxed ml-2">{announcement.text}</p>
                                        <div className="flex items-center gap-2"><div className="w-[1px] h-4 bg-gray-400"></div><img src="/clock.svg" alt="clock" className="w-4 h-4" /><span className="text-[10px] font-normal text-[#002450] whitespace-nowrap">{formatDate(announcement.created_at)}</span></div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-gray-500">{language === 'fa' ? 'اطلاعیه‌ای موجود نیست' : 'No announcements'}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sağ Sekme - Desktop Menü (Aynı) */}
                <div className="hidden lg:block w-full lg:w-[272px] shrink-0">
                    <MessagesSidebar
                    activeKey=""
                    onNavigate={(key) => {
                        if (key === 'messages') navigate('/messages');
                        if (key === 'finance') navigate('/finance');
                        if (key === 'account') navigate('/account');
                        if (key === 'support') navigate('/support');
                        if (key === 'logout') navigate('/logout');
                    }}
                />
                </div>
            </div>
        </div>
    );
}

export default Event;